/**
 * The Plan Overview — a read-only, expansive, easy-to-process presentation of a Resource
 * Plan, shared by link (see the "Share" button on `PlanEditor`).
 *
 * This is deliberately NOT the working editor: its grid is a read-only twin of the
 * working one (SharedPlanGrid), and there are no inline edits, no status control, no
 * dialogs. Every number on this screen is text. It is also bill-side only —
 * `SharedPlanView` (src/schemas/resource-plans.ts) carries no cost and no margin at any
 * grain, for any reader, so this component has nothing to hide and nothing to gate.
 */

import { Link } from "@tanstack/react-router";
import { FileText, Printer } from "lucide-react";
import { PlanStatusBadge } from "@/components/plans/PlansTable";
import { SharedPlanGrid } from "@/components/plans/SharedPlanGrid";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSharedPlan } from "@/hooks/use-resource-plans";
import type {
  SharedPlanLineView,
  SharedPlanView,
  SharedPlanWorkstreamView,
} from "@/schemas/resource-plans";

// ── Formatters ──────────────────────────────────────────────────────────────────

function money(value: string | null): string {
  if (value === null) return "—";
  const negative = value.startsWith("-");
  const [whole = "0", frac = "00"] = value.replace("-", "").split(".");
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${negative ? "−" : ""}$${grouped}.${frac.padEnd(2, "0").slice(0, 2)}`;
}

/*
 * ROUNDED, not truncated.
 *
 * Server hours are 2dp. Truncating the fraction makes a shared document fail to add up
 * in front of the reader: two 10.50h lines would each print "10 hrs" under a "21 hrs"
 * total. On the working grid that is a rounding quibble; on a page whose whole purpose is
 * to be sent and printed, visibly non-summing columns are the thing that loses trust.
 */
function hours(value: string): string {
  const rounded = Math.round(Number(value));
  const safe = Number.isFinite(rounded) ? rounded : 0;
  const grouped = Math.abs(safe)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return `${safe < 0 ? "−" : ""}${grouped} hrs`;
}

function percent(value: string | null): string {
  if (value === null) return "—";
  return `${value}%`;
}

function formatMonth(value: string | null): string {
  if (!value) return "—";
  const [year, month] = value.split("-");
  if (!year || !month) return value;
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "numeric" });
}

function formatDate(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function peakFte(byMonth: SharedPlanView["byMonth"]): string {
  const max = byMonth.reduce((acc, m) => Math.max(acc, Number(m.fte)), 0);
  if (max === 0) return "—";
  return max.toFixed(1);
}

// ── Sections ────────────────────────────────────────────────────────────────────

function HeadlineTiles({ plan }: { plan: SharedPlanView }) {
  const monthCount = plan.months.length;
  const first = plan.months[0] ?? null;
  const last = plan.months[plan.months.length - 1] ?? null;

  const tiles: Array<{ label: string; value: string; note?: string }> = [
    { label: "Total Hours", value: hours(plan.totalHours) },
    { label: "Indicative Revenue", value: money(plan.indicativeRevenue) },
    { label: "Blended Rate", value: money(plan.blendedRate), note: "revenue ÷ hours" },
    {
      label: "Duration",
      value: `${monthCount} month${monthCount === 1 ? "" : "s"}`,
      note: first && last ? `${formatMonth(first)} – ${formatMonth(last)}` : undefined,
    },
    { label: "Plan Lines", value: String(plan.lineCount) },
    { label: "Peak FTE", value: peakFte(plan.byMonth), note: "reference only" },
  ];

  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-3 rounded-md border border-border bg-card px-4 py-3 sm:grid-cols-3 lg:grid-cols-6">
      {tiles.map((tile) => (
        <div key={tile.label}>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {tile.label}
          </div>
          <div className="mt-0.5 font-display text-lg font-bold leading-tight tabular-nums">
            {tile.value}
          </div>
          {tile.note && <div className="text-xs text-muted-foreground">{tile.note}</div>}
        </div>
      ))}
    </div>
  );
}

/*
 * Screen only. Paper cannot scroll a 27-month grid sideways, so the printed overview keeps
 * the per-Workstream line tables (PlanLinesSection) in its place.
 */
function PlanGridSection({ plan }: { plan: SharedPlanView }) {
  return (
    <section className="flex flex-col gap-2 print:hidden">
      <h2 className="text-lg font-semibold text-foreground">Plan Lines by month</h2>
      <SharedPlanGrid plan={plan} />
    </section>
  );
}

function DeliveryShapeSection({ plan }: { plan: SharedPlanView }) {
  const maxHours = plan.byMonth.reduce((acc, m) => Math.max(acc, Number(m.totalHours)), 0);

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-foreground">Delivery shape by month</h2>
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">Month</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Hours</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">FTE</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Revenue</th>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">Shape</th>
            </tr>
          </thead>
          <tbody>
            {plan.byMonth.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                  No months in this plan's window.
                </td>
              </tr>
            ) : (
              plan.byMonth.map((m) => {
                const width = maxHours > 0 ? (Number(m.totalHours) / maxHours) * 100 : 0;
                return (
                  <tr key={m.month} className="border-t border-border">
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-foreground">
                      {formatMonth(m.month)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      {hours(m.totalHours)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      {Number(m.fte).toFixed(1)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      {money(m.totalRevenue)}
                    </td>
                    <td className="px-3 py-2">
                      <div className="h-2 w-full min-w-24 overflow-hidden rounded-full bg-muted">
                        <div
                          className="h-full rounded-full bg-primary/70"
                          style={{ width: `${width}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PlannedPodSection({ plan }: { plan: SharedPlanView }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-foreground">Planned Pod</h2>
      {plan.pod === null ? (
        <p className="text-sm text-muted-foreground">No Planned Pod composed.</p>
      ) : (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-card p-4">
          <div className="text-sm font-medium text-foreground">{plan.pod.originSummary}</div>
          <div className="text-xs text-muted-foreground">
            {plan.pod.shapeName ?? "Custom"} · {plan.pod.shapeSource}
          </div>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">Position</th>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">Discipline</th>
                  <th className="px-3 py-2 font-semibold whitespace-nowrap">Labor Category</th>
                  <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                    Total Hours
                  </th>
                  <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">FTE</th>
                </tr>
              </thead>
              <tbody>
                {plan.pod.positions.map((position) => (
                  <tr key={position.id} className="border-t border-border">
                    <td className="px-3 py-2 whitespace-nowrap font-medium text-foreground">
                      {position.positionCode}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {position.expectedDisciplineName} ({position.expectedDisciplineCode})
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                      {position.laborCategoryName ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      {hours(position.totalHours)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                      {Number(position.fte).toFixed(1)}
                      {!position.hasPlannedWork && (
                        <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                          No planned work
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}

function WorkstreamsSection({ plan }: { plan: SharedPlanView }) {
  const totalHours = Number(plan.totalHours);
  /*
   * The plan-level "General" group is emitted unconditionally by the server (it is where
   * lines live before anyone groups them), so on a plan that DOES use Workstreams it
   * arrives carrying nothing and would render as a 0 hrs / 0.0% / $0.00 / — row. Drop any
   * group with no lines and no hours; PlanLinesSection already skips the same empties.
   */
  const sorted = [...plan.workstreams]
    .filter((ws) => ws.lineCount > 0 || Number(ws.totalHours) > 0)
    .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-foreground">Workstreams</h2>
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-semibold whitespace-nowrap">Workstream</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Hours</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">% of Total</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Revenue</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Blended Rate</th>
              <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">Lines</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((ws: SharedPlanWorkstreamView) => {
              const share = totalHours > 0 ? (Number(ws.totalHours) / totalHours) * 100 : 0;
              return (
                <tr key={ws.id ?? "general"} className="border-t border-border">
                  <td className="px-3 py-2 align-top">
                    <div className="font-medium text-foreground">{ws.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {[ws.type, ws.description].filter(Boolean).join(" · ")}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {hours(ws.totalHours)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {share.toFixed(1)}%
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {money(ws.totalRevenue)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {ws.blendedRate === null ? "—" : money(ws.blendedRate)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                    {ws.lineCount}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/*
 * PRINT only. On screen the grid carries every line, with its months; on paper the grid
 * would be cut off at the page edge, so these tables print in its place.
 */
function PlanLinesSection({ plan }: { plan: SharedPlanView }) {
  const sortedWorkstreams = [...plan.workstreams].sort(
    (a, b) => Number(a.sortOrder) - Number(b.sortOrder),
  );
  const linesByWorkstream = new Map<string | null, SharedPlanLineView[]>();
  for (const line of plan.lines) {
    const key = line.workstreamId;
    const existing = linesByWorkstream.get(key) ?? [];
    existing.push(line);
    linesByWorkstream.set(key, existing);
  }
  for (const lines of linesByWorkstream.values()) {
    lines.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder));
  }

  return (
    <section className="hidden flex-col gap-2 print:flex">
      <h2 className="text-lg font-semibold text-foreground">Plan Lines</h2>
      <div className="flex flex-col gap-4">
        {sortedWorkstreams.map((ws) => {
          const lines = linesByWorkstream.get(ws.id) ?? [];
          if (lines.length === 0) return null;
          return (
            <div
              key={ws.id ?? "general"}
              className="overflow-x-auto rounded-md border border-border bg-card"
            >
              <div className="border-b border-border bg-muted/40 px-3 py-2 text-sm font-semibold text-foreground">
                {ws.name}
              </div>
              <table className="w-full text-sm">
                <thead className="bg-muted/20 text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">Labor Category</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">Position</th>
                    <th className="px-3 py-2 font-semibold whitespace-nowrap">
                      Client Labor Category
                    </th>
                    <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      Bill Rate
                    </th>
                    <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      Total Hours
                    </th>
                    <th className="px-3 py-2 text-right font-semibold whitespace-nowrap">
                      Total Revenue
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.id} className="border-t border-border">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {line.laborCategoryName}
                        {line.disciplineCode && (
                          <span className="ml-1.5 text-xs text-muted-foreground">
                            ({line.disciplineCode})
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                        {line.position ?? "—"}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                        {line.clientLaborCategory ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {money(line.billRate)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {hours(line.totalHours)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                        {money(line.totalRevenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CommercialSummarySection({ plan }: { plan: SharedPlanView }) {
  const rows: Array<{ label: string; value: string }> = [];
  rows.push({
    label: "Pricing Model",
    value: plan.pricingModel === "FixedPrice" ? "Fixed Price" : "Time & Materials",
  });
  rows.push({ label: "Budget Cadence", value: plan.budgetCadence });
  rows.push({ label: "Rate Basis", value: plan.rateBasisLabel });
  if (plan.engagementStandardTierName !== null) {
    rows.push({ label: "Engagement Standard Tier", value: plan.engagementStandardTierName });
  }
  if (plan.contractedFee !== null) {
    rows.push({
      label: "Contracted Fee",
      value: `${money(plan.contractedFee)}${plan.contractedFeeBasis ? ` (${plan.contractedFeeBasis})` : ""}`,
    });
  }
  if (plan.feeVariance !== null) {
    rows.push({
      label: "Fee Variance",
      value: `${money(plan.feeVariance)}${plan.feeVariancePercent !== null ? ` (${percent(plan.feeVariancePercent)})` : ""}`,
    });
  }
  if (plan.discountPercent !== null || plan.discountAmount !== null) {
    const parts = [
      plan.discountPercent !== null ? percent(plan.discountPercent) : null,
      plan.discountAmount !== null ? money(plan.discountAmount) : null,
    ].filter((v): v is string => v !== null);
    rows.push({ label: "Discount", value: parts.join(" / ") });
  }
  if (plan.sowReference !== null) {
    rows.push({ label: "SOW Reference", value: plan.sowReference });
  }
  if (plan.wonDate !== null) {
    rows.push({ label: "Won", value: formatDate(plan.wonDate) });
  }
  if (plan.estimatedHours !== null) {
    rows.push({ label: "Estimated Hours", value: hours(plan.estimatedHours) });
  }

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold text-foreground">Commercial Summary</h2>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 rounded-md border border-border bg-card p-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => (
          <div key={row.label} className="flex flex-col">
            <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {row.label}
            </dt>
            <dd className="text-sm text-foreground">{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

// ── Root component ─────────────────────────────────────────────────────────────

export function PlanOverview({ planId }: { planId: string }) {
  const { data: plan, isLoading, isError, error, refetch } = useSharedPlan(planId);

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load this Plan Overview."}
          onRetry={refetch}
        />
      </PageContainer>
    );
  }

  if (plan === undefined) {
    return (
      <PageContainer>
        <EmptyState
          icon={FileText}
          title="No overview available"
          description="This plan has no shareable overview yet."
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title={
          <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span>{plan.name}</span>
            <PlanStatusBadge status={plan.status} />
          </span>
        }
        description={`${plan.clientAccountName} · ${plan.solutionName} · ${plan.kind}`}
        actions={
          <div className="flex flex-wrap items-center gap-2 print:hidden">
            <Link
              to="/dashboard/plans/$planId"
              params={{ planId: plan.id }}
              className="inline-flex h-9 items-center gap-1 rounded-md border border-border px-3 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              Open working view
            </Link>
            <Button size="default" variant="outline" onClick={() => window.print()}>
              <Printer className="h-3.5 w-3.5" />
              Print
            </Button>
          </div>
        }
      />

      <HeadlineTiles plan={plan} />
      <PlanGridSection plan={plan} />
      <DeliveryShapeSection plan={plan} />
      <PlannedPodSection plan={plan} />
      <WorkstreamsSection plan={plan} />
      <PlanLinesSection plan={plan} />
      <CommercialSummarySection plan={plan} />

      <div className="border-t border-border pt-3 text-xs text-muted-foreground">
        {plan.versionNumber !== undefined && <>Plan version {plan.versionNumber} · </>}
        last updated {formatDate(plan.updatedAt)}
        {plan.ownerName !== null && <> · owner {plan.ownerName}</>}
      </div>
    </PageContainer>
  );
}
